import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import { callAPI, getSuggestions } from "../utils/api";
import { setTitle, trimInput } from "../utils/utils";
import { PersonalityType, PersonalityOptions } from "../components/PersonalitySelector/PersonalitySelector";

// Helper functions for localStorage operations
const getStoredPersonality = () => {
  try {
    const stored = localStorage.getItem('selectedPersonality');
    // Validate that the stored value is a valid personality type
    if (stored && Object.values(PersonalityType).includes(stored)) {
      return stored;
    }
  } catch (error) {
    console.warn('Failed to load personality from localStorage:', error);
  }
  return PersonalityType.CASUAL; // fallback to default
};

const savePersonalityToStorage = (personality) => {
  try {
    localStorage.setItem('selectedPersonality', personality);
  } catch (error) {
    console.warn('Failed to save personality to localStorage:', error);
  }
};

function useChatLogic() {
  const [prompt, setPrompt] = useState('');
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedWord, setSelectedWord] = useState('');
  const [selectedPersonality, setSelectedPersonality] = useState(getStoredPersonality);
  const [originalWord, setOriginalWord] = useState(''); // Track the original word for personality switching

  const inputRef = useRef(null);
  const initialSearchDone = useRef(false);
  const isSubmitting = useRef(false);

  // Memoize URL operations to prevent recreation
  const urlOperations = useMemo(() => ({
    updateUrl: (word) => {
      const newUrl = `${window.location.pathname}?w=${encodeURIComponent(word)}`;
      window.history.pushState({}, '', newUrl);
    },
    clearUrlParam: () => {
      window.history.pushState({}, '', window.location.pathname);
    }
  }), []);

  const { updateUrl, clearUrlParam } = urlOperations;

  const addMessage = useCallback((role, content, isStreaming = false) => {
    setConversation(prev => {
      // If streaming and the last message is from assistant, update it
      if (isStreaming && prev.length > 0 && prev[prev.length - 1].role === 'assistant') {
        return [...prev.slice(0, -1), { role, content }];
      }
      // Otherwise add new message
      return [...prev, { role, content }];
    });
  }, []);

  const handleAPICall = useCallback(async (newPrompt) => {
    setIsLoading(true);
    let streamingMessage = '';

    // Check if this is the first assistant response
    const assistantMessageCount = conversation.filter(msg => msg.role === 'assistant').length;
    const personalityEmoji = PersonalityOptions[selectedPersonality]?.icon + ' ';

    try {
      await callAPI(
        [...conversation, { role: 'user', content: newPrompt }],
        selectedPersonality, // Pass the selected personality
        (chunk) => {
          streamingMessage += chunk;
          // Prepend emoji only for the first response
          const messageWithEmoji = personalityEmoji + streamingMessage;
          addMessage('assistant', messageWithEmoji, true);
        }
      );
    } catch (error) {
      console.error('API call failed:', error);
      const errorMessage = personalityEmoji + `Error: ${error.message}`;
      addMessage('assistant', errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [conversation, addMessage, selectedPersonality]);

  const handleSubmit = useCallback((input) => {
    const trimmedInput = input.trim();
    if (isLoading || !trimmedInput) return;

    isSubmitting.current = true;
    setIsLoading(true);
    setSuggestions([]);
    setPrompt('');
    setSelectedWord('');

    // Store the original word for personality switching
    if (conversation.length === 0) {
      setOriginalWord(trimmedInput);
    }

    addMessage('user', trimmedInput);

    handleAPICall(trimmedInput)
      .then(() => {
        if (conversation.length === 0) {
          setTitle(trimmedInput);
          updateUrl(trimmedInput);
        }
      })
      .finally(() => {
        isSubmitting.current = false;
      });
  }, [isLoading, addMessage, handleAPICall, conversation.length, updateUrl]);

  const fetchSuggestions = useCallback(async (text) => {
    if (!text.trim() || isSubmitting.current) {
      setSuggestions([]);
      return;
    }
    try {
      const data = await getSuggestions(text);
      // Check again in case submission happened during API call
      if (!isSubmitting.current) {
        setSuggestions(data.reverse());
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    }
  }, []);

  // Create debounced function with proper cleanup
  const debouncedFetchSuggestions = useMemo(() => {
    const debouncedFn = debounce(fetchSuggestions, 300);
    return debouncedFn;
  }, [fetchSuggestions]);

  // Clean up debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedFetchSuggestions.cancel();
    };
  }, [debouncedFetchSuggestions]);

  const handleSelectWord = useCallback((word) => {
    setSelectedWord(word);
    setPrompt(word);
    setSuggestions([]);
  }, []);

  const handlePersonalityChange = useCallback((personality) => {
    setSelectedPersonality(personality);
    savePersonalityToStorage(personality);

    // If we're in the middle of a conversation, re-make the API call with the new personality
    if (conversation.length > 0 && originalWord && !isLoading) {
      setIsLoading(true);

      // Reset conversation to just the original user message
      const userMessage = { role: 'user', content: originalWord };
      setConversation([userMessage]);

      // Make new API call with the new personality
      let streamingMessage = '';
      const personalityEmoji = PersonalityOptions[personality]?.icon + ' ';

      callAPI(
        [userMessage],
        personality,
        (chunk) => {
          streamingMessage += chunk;
          const messageWithEmoji = personalityEmoji + streamingMessage;
          setConversation([userMessage, { role: 'assistant', content: messageWithEmoji }]);
        }
      ).catch(error => {
        console.error('API call failed:', error);
        const errorMessage = personalityEmoji + `Error: ${error.message}`;
        setConversation([userMessage, { role: 'assistant', content: errorMessage }]);
      }).finally(() => {
        setIsLoading(false);
      });
    }
  }, [conversation.length, originalWord, isLoading]);

  const reset = useCallback(() => {
    setConversation([]);
    setPrompt('');
    setIsLoading(false);
    setSuggestions([]);
    setSelectedWord('');
    setOriginalWord('');
    // Note: we deliberately don't reset selectedPersonality here
    // as it should persist across resets
    setTitle('');
    clearUrlParam();
    inputRef.current?.focus();
  }, [clearUrlParam]);

  // Initialize from URL parameter
  useEffect(() => {
    inputRef.current?.focus();

    if (!initialSearchDone.current) {
      const searchParams = new URLSearchParams(window.location.search);
      const word = searchParams.get('w');
      if (word) {
        const validatedWord = trimInput(word);
        if (validatedWord) {
          setPrompt(validatedWord);
          handleSubmit(validatedWord);
        } else {
          clearUrlParam();
        }
      }
      initialSearchDone.current = true;
    }
  }, [handleSubmit, clearUrlParam]);

  return {
    prompt,
    setPrompt,
    conversation,
    isLoading,
    inputRef,
    suggestions,
    selectedWord,
    selectedPersonality,
    originalWord,
    handleSubmit,
    reset,
    handleSelectWord,
    handlePersonalityChange,
    debouncedFetchSuggestions,
    setSuggestions,
  };
}

export default useChatLogic;